import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../../../../components/ui/Card';
import { Button } from '@/components/ui';
import { Input, Field, FieldLabel, PhoneInput, PhoneNumberLabel } from '../../../../components/ui';
import { Badge } from '../../../../components/ui/Badge';
import {
  ArrowLeft,
  Save,
  Edit
} from 'lucide-react';
import { useAuthStore } from '../../../../store/auth';
import { AvatarUpload } from '../../../../components/AvatarUpload';
import { uploadUserAvatar } from '../../../../services/storage';
import { toast } from 'react-hot-toast';
import { hasAnyRole } from '@court-sync/shared';
import { GuardianSmsSettings } from '../../../../components/guardian/GuardianSmsSettings';
import { PageHeader } from '@/components/layout/PageHeader';

export default function Profile() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuthStore();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      // TODO: Implement profile update API call
      await updateUser(formData);
      setEditing(false);
      toast.success('Profile updated successfully!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
      address: '',
    });
    setEditing(false);
  };

  if (!user || !hasAnyRole(user, ['guardian', 'student'])) {
    navigate('/dashboard');
    return null;
  }

  return (
    <div className="contents">
      <PageHeader
        title="Profile Settings"
        subtitle="Manage your personal information and preferences"
      />
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Profile Overview */}
          <div className="md:col-span-1">
            <Card>
              <CardContent className="p-6 text-center">
                <div className="flex justify-center mb-4">
                  <AvatarUpload
                    avatarUrl={user.avatarUrl}
                    firstName={user.firstName}
                    lastName={user.lastName}
                    onUpload={async (file) => uploadUserAvatar(file, user.uid)}
                    onSave={async (avatarUrl) => updateUser({ avatarUrl })}
                    onRemove={async () => updateUser({ avatarUrl: null })}
                    size="xl"
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {user.firstName} {user.lastName}
                </h3>
                <p className="text-gray-600 mb-4">{user.email}</p>
                <Badge variant="success" className="bg-green-100 text-green-800">
                  Student
                </Badge>
              </CardContent>
            </Card>
          </div>

          {/* Profile Form */}
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Personal Information
                  </h3>
                  {!editing ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditing(true)}
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancel}
                        disabled={loading}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={handleSave}
                        disabled={loading}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {loading ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field>
                      <FieldLabel htmlFor="firstName">First Name</FieldLabel>
                      <Input id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} disabled={!editing} placeholder="Enter your first name" />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="lastName">Last Name</FieldLabel>
                      <Input id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} disabled={!editing} placeholder="Enter your last name" />
                    </Field>
                  </div>

                  <Field>
                    <FieldLabel htmlFor="email">Email Address</FieldLabel>
                    <Input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} disabled placeholder="Enter your email" className="bg-gray-50" />
                    <p className="text-xs text-gray-500 mt-1">
                      Email address cannot be changed
                    </p>
                  </Field>

                  <Field>
                    <PhoneNumberLabel htmlFor="phone" purpose="account_contact">
                      Phone Number
                    </PhoneNumberLabel>
                    <PhoneInput
                      id="phone"
                      value={formData.phone}
                      onChange={(value) => setFormData((prev) => ({ ...prev, phone: value }))}
                      disabled={!editing}
                      placeholder="Phone number"
                    />
                  </Field>

                  <Field>
                    <FieldLabel htmlFor="address">Address</FieldLabel>
                    <Input id="address" name="address" value={formData.address} onChange={handleInputChange} disabled={!editing} placeholder="Enter your address" />
                  </Field>
                </div>
              </CardContent>
            </Card>

            {/* Account Settings */}
            <Card className="mt-6">
              <CardHeader>
                <h3 className="text-lg font-semibold text-gray-900">
                  Account Settings
                </h3>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Change Password</h4>
                      <p className="text-sm text-gray-600">Update your account password</p>
                    </div>
                    <Button variant="outline" size="sm">
                      Change Password
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h4 className="font-medium text-gray-900">Notification Preferences</h4>
                      <p className="text-sm text-gray-600">Manage your notification settings</p>
                    </div>
                    <Button variant="outline" size="sm">
                      Manage Notifications
                    </Button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                    <div>
                      <h4 className="font-medium text-red-900">Delete Account</h4>
                      <p className="text-sm text-red-600">Permanently delete your account</p>
                    </div>
                    <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:border-red-300">
                      Delete Account
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <GuardianSmsSettings />
          </div>
        </div>
      </div>
    </div>
  );
}
